import { useState, Fragment, useContext, useEffect } from "react";
import { HttpRequest } from "../../services/httpRequest";
import { useNavigate } from "react-router-dom";
import { motivoIngresoRechazado, trackEvent } from "../../services/analytics/track";
import { SuccessAlert } from "../../components/alerts/successAlert";
import { RecaptchaView } from "../../components/captcha/recaptcha";
import { ErrorAlert } from "../../components/alerts/errorAlert";
import { WarningAlert } from "../../components/alerts/warningAlert";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  contieneCaracteresPeligrosos,
  validarLongitudMaxima,
} from "../../utils/validacionesForms";
import { useUtmTracking } from "../../hooks/useUtmTracking";
import { Login } from "../../components/backgrounds/Login";
import StepInput from "../../components/new_components/StepInput";
import { useMobileDetection } from "../../hooks/use-mobile-detection";
import { NextButton } from "../../components/new_components/buttonsFlow";
import { ChevronLeft, Mail } from "lucide-react";
import { getStoredUtmReferral } from "../../services/utmleads/utmReferral";

export function SigninPage() {
  useUtmTracking();

  const { isValidReCaptcha, alertSignIn, updateAlertSignIn } =
    useContext(AuthContext);
  const navigate = useNavigate();

  const [doc, setDoc] = useState("");
  const [channelOtp, setChannelOtp] = useState({ channel: "W" });

  const { isDesktop, screenHeight } = useMobileDetection();

  const [alert, setAlert] = useState({
    type: null, // "success" | "error" | "warning"
    message: "",
    isOpen: false,
  });

  const getFormMarginTop = () => {
    if (screenHeight <= 199) return "460px";
    if (screenHeight <= 299) return "420px";
    if (screenHeight <= 399) return "380px";
    if (screenHeight <= 499) return "340px";
    if (screenHeight <= 515) return "300px";
    if (screenHeight <= 565) return "260px";
    if (screenHeight <= 599) return "220px";
    if (screenHeight <= 655) return "180px";
    if (screenHeight <= 699) return "140px";
    if (screenHeight <= 750) return "100px";
    if (screenHeight >= 751) return "10px";
  };

  const handleChange = (e) => {
    const { value } = e.target;
    if (value.length > 10) return; // Validar longitud máxima en el cambio
    setDoc(value);
  };

  const handleOpenAlert = (type, message) => {
    // Primero cerramos el alert para forzar re-render
    setAlert({
      type: null,
      message: "",
      isOpen: false,
    });

    // Luego lo abrimos en el siguiente ciclo
    setTimeout(() => {
      setAlert({
        type,
        message,
        isOpen: true,
      });
    }, 10);
  };

  const handleChangeChannel = (channel) => {
    setChannelOtp({ channel: channel });
    return;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = async (event) => {
    const maxDocLength = 10;

    try {
      event.preventDefault();

      if (!isValidReCaptcha)
        return handleOpenAlert(
          "error",
          "Debe validar el captcha para continuar .",
        );

      // 1. Validar que no esté vacío
      if (!doc) {
        handleOpenAlert("warning", "Ingresa tu número de documento");
        return;
      }

      // 2. Validar Longitud (Buffer Overflow protection)
      if (!validarLongitudMaxima(doc, maxDocLength)) {
        handleOpenAlert(
          "error",
          `El documento no puede exceder ${maxDocLength} caracteres`,
        );
        return;
      }

      // 3. Validar Inyección de caracteress
      if (contieneCaracteresPeligrosos(doc)) {
        handleOpenAlert(
          "error",
          "El documento contiene caracteres no válidos.",
        );
        return;
      }

      let payload = Object.assign(
        {},
        {
          doc: doc,
          channelOtp: channelOtp.channel,
          lastPartnerReferral: getStoredUtmReferral(),
        },
      );

      const responseService = await HttpRequest.loginRequestSms(payload);
      if (responseService.error || responseService.code === "ERR_NETWORK") {
        trackEvent("ingreso_rechazado", { motivo: motivoIngresoRechazado(responseService) });
      }

      if (responseService.code === "ERR_NETWORK") {
        handleOpenAlert(
          "error",
          "Ups! Ha ocurrido un error de conexión, intente más tarde.",
        );
        setDoc("");
        return;
      }

      if (responseService.error) {
        if (responseService.message.code === "NOTEXISTUSER") {
          handleOpenAlert(
            "error",
            "Ups! El usuario no existe con el documento ingresado.",
          );
          setDoc("");
          return setTimeout(() => {
            navigate("/registrarse");
          }, 2000);
        } else if (responseService.message.code === "ACCOUNTBLOCK") {
          handleOpenAlert("error", `Ups! ${responseService.message.message}`);
          setDoc("");
          return;
        } else if (responseService.message.code === "INVALIDCREDENTIALS") {
          handleOpenAlert("error", `Ups! ${responseService.message.message}`);
          setDoc("");
          return;
        } else if (responseService.message.code === "OTP_RATE_LIMIT") {
          handleOpenAlert("error", `Ups! ${responseService.message.message}`);
          return;
        }

        handleOpenAlert("error", "Ups! Error interno, intenta nuevamente");
        setDoc("");
        return;
      }

      let phoneNumber = responseService.message.response.phone;
      let emailUser = responseService.message.response.mail;

      handleOpenAlert(
        "success",
        channelOtp.channel === "W"
          ? "Te enviamos un código a tu telefono registrado!"
          : "Te enviamos un código a tu correo registrado!",
      );

      updateAlertSignIn({
        message: "",
        show: false,
        title: "",
      });

      setTimeout(() => {
        return navigate("/autenticarse", {
          replace: true,
          state: {
            phone: phoneNumber,
            doc: doc,
            email: emailUser,
            channel: channelOtp.channel,
          },
        });
      }, 2000);
    } catch (error) {
      console.log(error);
      return navigate("/ingresar");
    }
  };

  useEffect(() => {
    const previousBackground = document.body.style.background;

    document.body.style.background = "#000000";
    document.body.style.margin = "0";
    document.body.style.padding = "0";

    return () => {
      document.body.style.background = previousBackground;
      document.body.style.margin = "";
      document.body.style.padding = "";
    };
  }, []);

  useEffect(() => {
    if (alertSignIn) {
      if ("show" in alertSignIn) {
        let showAlertSession = alertSignIn?.show;
        if (showAlertSession === true) {
          setAlert({
            type: "warning",
            message: alertSignIn?.message,
            isOpen: true,
          });
        }
      }
    }

    const isSignin = localStorage.getItem("is_signin");
    const tokenSession = localStorage.getItem("token_jwt");

    if (isSignin && tokenSession) {
      return navigate("/micredito");
    }
    return;
  }, [alertSignIn, navigate]);

  return (
    <Fragment>
      {alert.isOpen && alert.type === "success" && (
        <SuccessAlert
          isOpen={alert.isOpen}
          message={alert.message}
          isDesktop={isDesktop}
        />
      )}

      {alert.isOpen && alert.type === "error" && (
        <ErrorAlert
          isOpen={alert.isOpen}
          message={alert.message}
          isDesktop={isDesktop}
        />
      )}

      {alert.isOpen && alert.type === "warning" && (
        <WarningAlert
          isOpen={alert.isOpen}
          message={alert.message}
          isDesktop={isDesktop}
        />
      )}
      {isDesktop && (
        <>
          <div
            class="z-20 flex flex-col md:flex-row items-center justify-center gap-2.5 text-[#ffffff]/70 pointer-events-none select-none tracking-widest uppercase font-light text-[10px] text-center my-6 md:my-0 md:fixed md:w-auto md:left-[-290px] md:top-[50%] md:-translate-y-1/2 md:-rotate-90 md:origin-center">
            <div class="flex flex-row items-center justify-center gap-4 mr-2">
              <div class="flex items-center md:border-r border-[#ffffff]/30 pr-4 shrink-0 capitalize">
                <img src="https://galilea.co/png/colombia-fintech.png" class="h-8 w-auto object-contain"
                  alt="colombia fintech" />
              </div>

              <div class="flex items-center md:border-r border-[#ffffff]/30 md:pr-4 shrink-0">
                <img src="https://galilea.co/png/sic.png" class="h-6 w-auto object-contain brightness-0 invert opacity-90"
                  alt="Superintendencia de Industria y Comercio" />
              </div>
            </div>
            <span
              class="normal-case text-[11px] leading-tight tracking-normal text-[#ffffff]/70 max-w-[280px] md:max-w-[340px] font-semibold2">
              Galilea es una marca de GRUPO EMPRESARIAL ANAV S.A.S GEAA <br class="hidden md:inline" />registrada en
              Colombia con NIT: 901.582.748-5
            </span>

          </div>
        </>
      )}

      <Login
        form={
          <form
            className="
              space-y-6
              w-full
              max-w-[440px]
              min-h-135.5
              mx-auto
              px-5
              sm:px-10
              py-5
            "
            onKeyDown={handleKeyDown}
            style={{
              background: "linear-gradient(145deg, rgba(28,18,43,0.95) 0%, rgba(13,9,20,0.98) 100%)",
              borderRadius: "25px",
              display: "flex",
              flexDirection: "column",
              padding: "20px 40px 20px 40px",
              border: "1px solid rgba(255,255,255,0.08)",
              marginTop: !isDesktop && getFormMarginTop(),
            }}
          >
            <div
              className="flex justify-start w-full"
              style={{
                gap: isDesktop ? "60px" : "30px",
              }}
            >
              <button
                onClick={() => {
                  navigate("/");
                }}
                style={{ cursor: "pointer" }}
              >
                <ChevronLeft
                  className={`w-7 h-7 text-white`}
                  strokeWidth={2.5}
                />
              </button>
              <img
                className="h-12 w-48 cursor-pointer"
                src="/svg/imagotipo-galilea-02.svg"
                alt="Galilea"
                onClick={() => {
                  navigate("/");
                }}
              />
            </div>
            <div>
              <div>
                <h2
                  className="text-2xl md:text-2xl font-bold text-center text-white"
                  autoFocus
                >
                  <div>{"Ingresa a tu cuenta"}</div>
                </h2>
                <p className="mt-1 text-sm leading-6 text-center text-grayP">
                  {"Digita tu número de documento para verificar tu cuenta"}
                </p>
              </div>

              <div className="mt-5">
                <StepInput
                  name="documento"
                  value={doc}
                  label={"Número de documento"}
                  onChange={handleChange}
                  required
                  placeholder="Ej. 1023456789"
                  autoFocus={true}
                  type="number"
                  minLength={0}
                  paddingRight="0px"
                  paddingLeft="0px"
                />
              </div>
              <div>
                <RecaptchaView></RecaptchaView>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                margin: "0px",
                gap: "15px",
              }}
            >
              <div className="text-center">
                <span className="font-sm text-sm text-white">
                  Elige cómo quieres recibir el código de verificación
                </span>
              </div>
              <div className="flex flex-row gap-3">
                <div className="w-full">
                  <button
                    onClick={(e) => handleChangeChannel("W")}
                    type="button"
                    className="w-full flex justify-center py-2 px-4 rounded-full text-lg font-semibold bg-purple transition duration-300 hover:scale-110"
                    style={{
                      background:
                        channelOtp.channel === "W"
                          ? "linear-gradient(103.2deg, rgba(124, 58, 237, 0.3) 0%, rgba(168, 85, 247, 0.2) 100%)"
                          : "#FFFFFF0F",
                      border:
                        channelOtp.channel === "W"
                          ? "1px solid rgba(124, 58, 237, 0.6)"
                          : "1px solid #FFFFFF1A",

                      boxShadow:
                        channelOtp.channel === "W"
                          ? "0px 0px 16px rgba(124, 58, 237, 0.2)"
                          : "none",
                      color: "white",
                      cursor: "pointer"
                    }}
                  >
                    <div
                      className="font-semibold2 flex items-center text-sm md:text-lg"
                      style={{ fontWeight: 500, fontSize: !isDesktop ? "11px" : "18px", gap: !isDesktop ? "5px" : "10px" }}
                    >
                      {/* <MessageCircle className="w-6 h-6" /> */}
                      <img
                        src="/jpeg/whatsapp-icon.png"
                        className="bg-transparent w-6 h-6"
                        alt=""
                      />
                      Whatsapp
                    </div>
                  </button>
                </div>

                <div className="w-full">
                  <button
                    onClick={(e) => handleChangeChannel("E")}
                    type="button"
                    className="w-full text-lg flex justify-center py-2 px-4 rounded-full font-semibold text-tdark tracking-tight bg-purpleLight transition duration-300 hover:scale-110"
                    style={{
                      background:
                        channelOtp.channel === "E"
                          ? "linear-gradient(103.2deg, rgba(124, 58, 237, 0.3) 0%, rgba(168, 85, 247, 0.2) 100%)"
                          : "#FFFFFF0F",
                      border:
                        channelOtp.channel === "E"
                          ? "1px solid rgba(124, 58, 237, 0.6)"
                          : "1px solid #FFFFFF1A",
                      boxShadow:
                        channelOtp.channel === "E"
                          ? "0px 0px 16px rgba(124, 58, 237, 0.2)"
                          : "none",
                      color: "white",
                      cursor: "pointer"
                    }}
                  >
                    <div
                      className="flex items-center gap-3 text-sm md:text-lg"
                      style={{ fontWeight: 500, fontSize: !isDesktop ? "11px" : "18px", gap: !isDesktop ? "5px" : "10px" }}
                    >
                      <Mail className="w-6 h-6" />
                      Email
                    </div>
                  </button>
                </div>
              </div>
              <div className="w-full">
                <NextButton
                  label={"Continuar"}
                  isLastStep={false}
                  showIconNext={false}
                  onNext={(e) => {
                    handleSubmit(e);
                  }}
                  // disabled={disableBtn}
                  isUnicButtom={true}
                />
              </div>
            </div>
            <div className="flex justify-center" style={{ margin: "0px" }}>
              <svg
                className="w-full max-w-93.5"
                height="28"
                viewBox="0 0 374 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  y="19.5"
                  width="167"
                  height="1"
                  fill="white"
                  fillOpacity="0.1"
                />
                <path
                  d="M186.552 24.144C185.976 24.144 185.464 24.012 185.016 23.748C184.568 23.484 184.216 23.112 183.96 22.632C183.712 22.144 183.588 21.576 183.588 20.928C183.588 20.272 183.716 19.704 183.972 19.224C184.228 18.744 184.58 18.372 185.028 18.108C185.484 17.836 186 17.7 186.576 17.7C187.152 17.7 187.664 17.836 188.112 18.108C188.56 18.372 188.908 18.744 189.156 19.224C189.412 19.704 189.54 20.272 189.54 20.928C189.54 21.576 189.412 22.144 189.156 22.632C188.9 23.112 188.544 23.484 188.088 23.748C187.64 24.012 187.128 24.144 186.552 24.144ZM186.552 23.28C186.912 23.28 187.24 23.192 187.536 23.016C187.832 22.84 188.068 22.576 188.244 22.224C188.42 21.872 188.508 21.44 188.508 20.928C188.508 20.4 188.42 19.964 188.244 19.62C188.076 19.268 187.844 19.004 187.548 18.828C187.252 18.652 186.928 18.564 186.576 18.564C186.224 18.564 185.9 18.652 185.604 18.828C185.308 19.004 185.068 19.268 184.884 19.62C184.708 19.964 184.62 20.4 184.62 20.928C184.62 21.44 184.708 21.872 184.884 22.224C185.06 22.576 185.292 22.84 185.58 23.016C185.876 23.192 186.2 23.28 186.552 23.28Z"
                  fill="#F8F6FF"
                  fillOpacity="0.35"
                />
                <rect
                  x="207"
                  y="19.5"
                  width="167"
                  height="1"
                  fill="white"
                  fillOpacity="0.1"
                />
              </svg>
            </div>
            <div className="flex justify-center" style={{ marginTop: "20px" }}>
              <span className="flex flex-col sm:flex-row items-center text-center sm:text-left">
                <p className="text-white">¿No eres cliente?</p>
                <a
                  href="/registrarse"
                  className="text-[#A78BFA] sm:pl-2 mt-1 sm:mt-0"
                >
                  Únete a nuestro mundo
                </a>
              </span>
            </div>
          </form>
        }
      />
    </Fragment>
  );
}
